// src/pages/SocialMediaMarketing.tsx
import React from "react";
import { motion } from "framer-motion";
import "./SocialMediaMarketing.css";

export default function SocialMediaMarketing(): JSX.Element {
  return (
    <main className="smm-page">
      {/* HERO */}
      <section className="smm-hero">
        <div className="smm-hero-inner">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Digital Marketing
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            Strategic, performance-led social media that connects your brand
            with the right audience and drives measurable business growth.
          </motion.p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="smm-services">
        <h2>What We Deliver</h2>

        <div className="smm-service-list">
          {[
            {
              title: "Social Media Strategy",
              text:
                "Audience research, channel selection, content pillars and a roadmap aligned to real business objectives — not vanity metrics.",
            },
            {
              title: "Organic Social Management",
              text:
                "Consistent, high-quality content across LinkedIn, Instagram, Facebook, X and YouTube to grow awareness, engagement and trust.",
            },
            {
              title: "Paid Social Advertising",
              text:
                "Performance-driven paid campaigns across Meta and LinkedIn to increase reach, traffic and conversions with clear ROI tracking.",
            },
            {
              title: "Creative & Content Production",
              text:
                "Scroll-stopping visuals, captions and short-form video optimised for each platform and campaign objective.",
            },
            {
              title: "Community & Brand Building",
              text:
                "Active engagement, comment moderation and optimisation strategies that strengthen brand perception and loyalty.",
            },
            {
              title: "Reporting & Optimisation",
              text:
                "Transparent reporting with actionable insights — what’s working, what’s not, and how to scale results.",
            },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              className="smm-service-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              viewport={{ once: true }}
            >
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="smm-process">
        <h2>Our Approach</h2>

        <div className="smm-process-steps">
          {[
            ["Discover", "Understand your brand, audience and commercial goals"],
            ["Plan", "Build a strategy, content pillars and channel roadmap"],
            ["Create", "Produce content and campaigns built to perform"],
            ["Optimise", "Refine based on data, performance and insights"],
            ["Scale", "Double-down on what works to grow results"],
          ].map(([title, text], i) => (
            <motion.div
              key={title}
              className="smm-step"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <span className="smm-step-number">{i + 1}</span>
              <div>
                <h4>{title}</h4>
                <p>{text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PLATFORMS */}
      <section className="smm-platforms">
        <h2>Platforms We Work With</h2>
        <div className="smm-platform-list">
          LinkedIn · Facebook · Instagram · YouTube · Meta Ads · X · LinkedIn Ads
        </div>
      </section>

      {/* CTA */}
      <section className="smm-cta">
        <h2>Ready to grow your brand?</h2>
        <p>
          Tell us your goals and we’ll design a social media strategy built for
          performance, not guesswork.
        </p>
        <a href="/contact" className="smm-cta-btn">
          Talk to an Expert
        </a>
      </section>
    </main>
  );
}
